import AddIcon from '@mui/icons-material/Add';
import {
	Box,
	Button,
	CircularProgress,
	Collapse,
	Stack,
	TextField,
	Typography,
	useTheme
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useAppConfig } from 'api/appConfig';
import { useMethodUserAttributes } from 'api/attributes';
import { useIsAuthenticationOnly } from 'api/configurations';
import { useCurrentGuideIdP } from 'api/idps';
import { EXTERNAL_ID, GENERIC_IDP_ID, GuideType } from 'consts';
import {
	useCurrentGuideIdPID,
	useIdPConfigContext
} from 'contexts/IdPConfigContext';
import {
	useCurrentGuideMethod,
	useSSOConfigMappings
} from 'contexts/SSOConfigFieldsContext';
import { ID, useMappingEditor } from 'hooks/useMappingEditor';
import React, { useCallback, useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { IUserAttributeMapping } from 'types/api';
import { getInitialSortedMappings } from 'utils/mappingUtils';
import MDGapWrapper from '../MDRenderer/utilComponents/MDGapWrapper';
import MappingRow from './MappingRow';

type DestOption = {
	value: string;
	label: string;
};

const UserAttributeMapping: React.FC = () => {
	// Use the guide-type-aware idp/method (never the raw SSO idpID/method) so the mapping
	// step initializes in the XAA/SCIM contexts too - not only for SSO.
	const idpID = useCurrentGuideIdPID();
	const method = useCurrentGuideMethod();
	const { guideType } = useIdPConfigContext();
	const isSCIMGuide = guideType === GuideType.SCIM;
	const { data: appConfig } = useAppConfig();
	const { data: idpData } = useCurrentGuideIdP();
	const { data: authenticationOnly } = useIsAuthenticationOnly();

	const { getUserAttributesMappings, setUserAttributesMappings, isSubTenant } =
		useSSOConfigMappings();

	const {
		data: userAttributes,
		isLoading: isUserAttributesLoading,
		isIdle: isUserAttributesIdle
	} = useMethodUserAttributes(method);

	const userAttributesMappings = getUserAttributesMappings();

	const customAttributeValues = useMemo(
		() =>
			new Set(
				userAttributes
					?.filter((userAttribute) => userAttribute.custom)
					.map((userAttribute) => userAttribute.value)
			),
		[userAttributes]
	);

	// SCIM consumes only the custom-attributes portion of the mapping —
	// standard attributes come directly from the SCIM payload — so SCIM
	// guides show and edit custom-attribute rows only
	const visibleMappings = useMemo(() => {
		if (!isSCIMGuide) {
			return userAttributesMappings;
		}
		return (userAttributesMappings || []).filter(
			(mapping) => !mapping.spAttr || customAttributeValues.has(mapping.spAttr)
		);
	}, [customAttributeValues, isSCIMGuide, userAttributesMappings]);

	// Mandatory attributes get an empty row seeded so there is always a visible field to fill.
	// In SCIM guides only the custom ones can be seeded: a standard attribute comes from the SCIM
	// payload and has no row here, so seeding it would render a required field nothing can satisfy
	const seedableAttributes = useMemo(
		() =>
			isSCIMGuide
				? userAttributes?.filter((userAttribute) => userAttribute.custom)
				: userAttributes,
		[isSCIMGuide, userAttributes]
	);

	const initializeItems = useCallback(
		(items: IUserAttributeMapping[]) =>
			getInitialSortedMappings(seedableAttributes!, items),
		[seedableAttributes]
	);

	const { rows, addItem, editItem, deleteItem, persist, isReady } =
		useMappingEditor<IUserAttributeMapping>({
			initialItems: userAttributes && method ? visibleMappings || [] : null,
			setExternalItems: setUserAttributesMappings,
			initializeItems,
			createEmptyItem: useCallback(() => ({ idpAttr: null, spAttr: null }), []),
			enabled: !!(userAttributes && method)
		});

	const destOptions = useMemo(
		() =>
			userAttributes?.filter(
				(userAttribute) =>
					(!isSCIMGuide || userAttribute.custom) &&
					(userAttribute.value !== EXTERNAL_ID ||
						!rows?.some((m) => m.spAttr === EXTERNAL_ID))
			),
		[userAttributes, rows, isSCIMGuide]
	);

	const showLoader =
		isUserAttributesLoading || isUserAttributesIdle || !isReady;

	const {
		palette: {
			grey: { 700: grey700 }
		}
	} = useTheme();

	const disabled = isSubTenant;
	const hideAddButton = appConfig?.disableUserMappingCreate;

	const srcLabel = intl.tr('ssoConfig.fields.userMapping.idpAttributeLabel', {
		idpName: idpData?.name,
		isGeneric: idpData?.id === GENERIC_IDP_ID
	});
	const destLabel = intl.tr('ssoConfig.fields.userMapping.userAttributeLabel');
	const addLabel = intl.tr('ssoConfig.fields.userMapping.addMapping');
	// A SCIM guide can only map custom attributes, so with none defined there is nothing to map -
	// show the explanation on its own rather than an editor whose every row would be unusable.
	// Gated on the attributes having loaded: customAttributeValues is empty while the query is in
	// flight, and the loader below renders this subheader too, so judging it early would flash
	// "nothing to map here" at a guide that does have custom attributes.
	const scimHasNothingToMap =
		isSCIMGuide && !!userAttributes && customAttributeValues.size === 0;
	const subHeaderKey = (() => {
		if (scimHasNothingToMap) {
			return 'ssoConfig.fields.userMapping.scimNoCustomAttributesSubHeader';
		}
		if (isSCIMGuide) {
			return 'ssoConfig.fields.userMapping.scimSubHeader';
		}
		// The mapping still runs on an authentication-only connection - it is what shapes the user
		// object the flow receives - but nothing is stored, so the stock line about changes syncing
		// on every sign-in would be wrong.
		return authenticationOnly
			? 'ssoConfig.fields.userMapping.authenticationOnlySubHeader'
			: 'ssoConfig.fields.userMapping.subHeader';
	})();
	const subHeader = intl.tr(subHeaderKey, {
		idpName: idpData?.name,
		isGeneric: idpData?.id === GENERIC_IDP_ID
	});

	if (showLoader || !idpID || !method) {
		return (
			<MDGapWrapper data-testid="user-attribute-mapping">
				<Stack gap={2}>
					<Typography variant="h3">
						{intl.tr('ssoConfig.fields.userMapping.header')}
					</Typography>
					<Typography variant="body1">{subHeader}</Typography>
					<Stack justifyContent="center" alignItems="center" height={200}>
						<CircularProgress />
					</Stack>
				</Stack>
			</MDGapWrapper>
		);
	}

	if (scimHasNothingToMap) {
		return (
			<MDGapWrapper data-testid="user-attribute-mapping">
				<Stack gap={2}>
					<Typography variant="h3">
						{intl.tr('ssoConfig.fields.userMapping.header')}
					</Typography>
					<Typography variant="body1">{subHeader}</Typography>
				</Stack>
			</MDGapWrapper>
		);
	}

	return (
		<MDGapWrapper data-testid="user-attribute-mapping">
			<Stack gap={2}>
				<Stack>
					<Typography variant="h3">
						{intl.tr('ssoConfig.fields.userMapping.header')}
					</Typography>
					<Typography variant="body1">{subHeader}</Typography>
				</Stack>
				<Stack gap={1}>
					{!rows ? (
						<Stack justifyContent="center" alignItems="center" height={200}>
							<CircularProgress />
						</Stack>
					) : (
						<TransitionGroup>
							{rows.map((row, i) => {
								// using userAttributes instead of destOptions to avoid missing externalID option in autocomplete
								const userAttr = userAttributes?.find(
									(ua) => ua.value === row.spAttr
								);
								const isRequired = userAttr?.required;
								// An authentication-only connection validates against no mandatory
								// attributes, so the marker and the error would show red for rows
								// the save does not need - and that the IdP may never send, the
								// usual case with Okta. The Login ID keeps both: the OIDC exchange
								// builds its subject from that claim and fails without it.
								const showAsRequired =
									isRequired &&
									(!authenticationOnly || row.spAttr === EXTERNAL_ID);
								const hasError = showAsRequired && !row.idpAttr;

								return (
									<Collapse key={row[ID]}>
										<MappingRow
											onDelete={() => deleteItem(i)}
											disableDelete={isRequired}
											disabled={disabled}
										>
											<TextField
												size="small"
												disabled={disabled}
												label={srcLabel}
												value={row.idpAttr ?? ''}
												onChange={(e) =>
													editItem(i, 'idpAttr', e.target.value || null)
												}
												onBlur={persist}
												sx={{ flexGrow: 1, flexBasis: 0 }}
												slotProps={{ inputLabel: { shrink: true } }}
												required={showAsRequired}
												error={hasError}
											/>
											<Typography color={grey700} mt={1}>
												{intl.tr('ssoConfig.fields.mapping.mapTo')}
											</Typography>
											<Autocomplete<DestOption>
												size="small"
												options={destOptions ?? []}
												value={userAttr ?? null}
												getOptionLabel={(opt) =>
													typeof opt === 'string' ? opt : (opt?.label ?? '')
												}
												isOptionEqualToValue={
													/* istanbul ignore next  */
													(opt, val) => opt?.value === val?.value
												}
												onChange={(_, newValue) => {
													editItem(i, 'spAttr', newValue?.value ?? null, true);
												}}
												disabled={isRequired || disabled}
												sx={{ flexGrow: 1, flexBasis: 0 }}
												renderInput={(params) => (
													<TextField
														{...params}
														data-testid="attribute-mapping-editor-dest-input"
														label={destLabel}
														slotProps={{
															inputLabel: { shrink: true }
														}}
													/>
												)}
											/>
										</MappingRow>
									</Collapse>
								);
							})}
						</TransitionGroup>
					)}
					{!disabled && !hideAddButton && (
						<Box>
							<Button
								startIcon={<AddIcon />}
								onClick={addItem}
								data-testid="attribute-mapping-editor-add-button"
							>
								<Typography variant="body1">{addLabel}</Typography>
							</Button>
						</Box>
					)}
				</Stack>
			</Stack>
		</MDGapWrapper>
	);
};

export default UserAttributeMapping;
